import React, { FC, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Typography,
    Grid,
    TextField,
    Button,
    CircularProgress,
    Box,
    FormHelperText
} from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';
import AsyncSelect from 'react-select/async';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { RevRexBankAccAddGlI } from '../../../resources/form-props';
import { RevRexBankAccAddGlValidate } from '../../../resources/form-validator';
import { GL_VP, SELECT_CUSTOM_STYLE } from '../../../resources/constants';
import SelectCustomOption from '../../../components/SelectCustomOption';
import { useBankFeedApi } from '../../../resources/hooks/api/bankFeedApiHook';
import { useChartAccountApi } from '../../../resources/hooks/api/chartAccountApiHook';

interface Props {
    isShow: boolean;
    onHide: () => void;
    onSuccess: () => void;
    branch_id: number;
}

const AddBankAccount: FC<Props> = ({ isShow, onHide, onSuccess, branch_id }) => {
    const { fetchBankTemplates, addBankAccounts } = useBankFeedApi();
    const { glAccountsList } = useChartAccountApi();
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const { t } = useTranslation('components.Modal.AddBankAcc');

    const {
        control,
        register,
        handleSubmit,
        formState: { errors }
    } = useForm<RevRexBankAccAddGlI>({
        defaultValues: {
            branch_id,
            name: '',
            desc: '',
            number: '',
            gl_account: null
        },
        resolver: yupResolver(RevRexBankAccAddGlValidate)
    });

    const [selectCustomStyle] = useState<any>(SELECT_CUSTOM_STYLE('#d1ffbd'));

    const networkOptions = async (inputValue: string) => {
        return await _handleFetchBankTemplates(inputValue);
    };

    const _handleFetchBankTemplates = (searchText: string): Promise<any> => {
        return new Promise<any>((resolve) => {
            const params = {
                csv_type_name: 'Bank Deposit',
                with_template: 2,
                network_status: 1,
                search_text: searchText || ''
            };
            fetchBankTemplates(
                params,
                (_message: string, resp: any) => {
                    const data = resp.data.data.rows.map((e: any) => ({
                        label: e['name'],
                        value: e['id']
                    }));
                    resolve(data);
                },
                (message: string) => {
                    toast.error(message);
                    resolve([]);
                }
            );
        });
    };

    const fetchGlVp = (params: any): Promise<any[]> => {
        return new Promise<any[]>((resolve) => {
            glAccountsList(
                params,
                (_message: string, resp: any) => {
                    resolve(resp?.data?.data?.result || []);
                },
                () => {
                    resolve([]);
                }
            );
        });
    };

    const fechAllAccountList = async (searchText: string = ''): Promise<any[]> => {
        const baseParams = {
            page: 1,
            per_page: 300,
            branch_id,
            parent_account_in: GL_VP.BANK_ACCOUNT.account.join(',')
        };

        const [assignedRes, unassignedRes] = await Promise.all([
            fetchGlVp({ ...baseParams, include_assigned_vp: 1 }),
            fetchGlVp({ ...baseParams, exclude_assigned_vp: 1 })
        ]);

        const toAccountNumber = (value: any) => Number(value);

        const assignedAccounts = new Set(
            assignedRes.map((r: any) => toAccountNumber(r.account)).filter((n: number) => !Number.isNaN(n))
        );

        const availableMap = new Map<number, { id: any; account: number; name: any }>();
        unassignedRes.forEach((r: any) => {
            const account = toAccountNumber(r.account);
            if (Number.isNaN(account)) return;
            availableMap.set(account, { id: r.id, account, name: r.name });
        });

        for (let i = 10030; i <= 11980; i += 10) {
            if (!assignedAccounts.has(i) && !availableMap.has(i)) {
                availableMap.set(i, { id: null, account: i, name: null });
            }
        }

        const available = Array.from(availableMap.values()).sort((a, b) => a.account - b.account);

        let options = available.map((e) => ({
            label: e.name ? `${e.name} (${e.account})` : `${e.account}`,
            value: e.id,
            account: e.account
        }));

        if (searchText) {
            const query = searchText.toLowerCase().trim();
            options = options.filter(
                (opt) => opt.label.toLowerCase().includes(query) || String(opt.account).includes(query)
            );
        }

        return options;
    };

    const glAccountOptions = async (inputValue: string) => {
        return await fechAllAccountList(inputValue);
    };

    const createNewItem = async (formValues: any) => {
        setShowLoader(true);
        const params: any = {
            ...formValues,
            name: formValues['name']['label'],
            number: '' + formValues['number'],
            gl_account_number: formValues?.gl_account?.account ?? '',
            gl_account_id: formValues?.gl_account?.value ?? null
        };
        delete params.gl_account;

        addBankAccounts(
            params,
            (message: string, resp: any) => {
                setShowLoader(false);
                if (resp?.data?.success) {
                    toast.success(message);
                    onSuccess();
                } else {
                    toast.error(message);
                }
            },
            (message: string) => {
                setShowLoader(false);
                toast.error(message);
            }
        );
    };

    // Resolves error message whether defined on gl_account or gl_account_number
    const glAccountErrorMessage =
        (errors.gl_account?.message as string) ||
        ((errors as any)?.gl_account_number?.message as string) ||
        (errors.gl_account ? t('glAccountRequired', 'GL Account is required') : '');

    return (
        <Dialog
            open={isShow}
            onClose={(_event, reason) => {
                if (reason !== 'backdropClick') onHide();
            }}
            fullWidth
            maxWidth="md"
        >
            <form onSubmit={handleSubmit(createNewItem)} noValidate>
                {/* Header */}
                <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6" component="span" fontWeight={600}>
                        {t('title')}
                    </Typography>
                    <IconButton aria-label="close" onClick={onHide} size="medium">
                        <i className="fa-regular fa-circle-xmark"></i>
                    </IconButton>
                </DialogTitle>

                {/* Body Content */}
                <DialogContent dividers sx={{ p: 3 }}>
                    <Grid container spacing={2}>
                        {/* Bank Select */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{ mb: 0.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                    {t('bank')}
                                    <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
                                </Typography>
                            </Box>
                            <Controller
                                control={control}
                                name="name"
                                rules={{ required: t('bankRequired', 'Bank is required') }}
                                render={({ field: { value, onChange } }) => (
                                    <AsyncSelect
                                        placeholder={t('bankSelect.placeholder')}
                                        isClearable
                                        defaultOptions
                                        loadOptions={networkOptions}
                                        onChange={onChange}
                                        menuPosition="fixed"
                                        value={value}
                                        styles={selectCustomStyle}
                                        components={{ Option: SelectCustomOption }}
                                    />
                                )}
                            />
                            {errors.name && (
                                <FormHelperText error sx={{ ml: 0 }}>
                                    {errors.name.message as string}
                                </FormHelperText>
                            )}
                        </Grid>

                        {/* Account Number */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{ mb: 0.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                    {t('accountNumber')}
                                    <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
                                </Typography>
                            </Box>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder={t('enterLastFourDigits')}
                                {...register('number')}
                            />
                            {errors.number && (
                                <FormHelperText error sx={{ ml: 0 }}>
                                    {errors.number.message as string}
                                </FormHelperText>
                            )}
                        </Grid>

                        {/* GL Account Select */}
                        <Grid item xs={12}>
                            <Box sx={{ mb: 0.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                    {t('glAccountList', 'Gl Account list')}
                                    <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
                                </Typography>
                            </Box>
                            <Controller
                                control={control}
                                name="gl_account"
                                rules={{ required: t('glAccountRequired', 'GL Account is required') }}
                                render={({ field: { value, onChange } }) => (
                                    <AsyncSelect
                                        placeholder={t('glAccountSelect.placeholder', 'Select GL Account')}
                                        isClearable
                                        defaultOptions
                                        loadOptions={glAccountOptions}
                                        onChange={onChange}
                                        menuPosition="fixed"
                                        value={value}
                                        styles={selectCustomStyle}
                                        components={{ Option: SelectCustomOption }}
                                    />
                                )}
                            />
                            {glAccountErrorMessage && (
                                <FormHelperText error sx={{ ml: 0 }}>
                                    {glAccountErrorMessage}
                                </FormHelperText>
                            )}
                        </Grid>

                        {/* Description */}
                        <Grid item xs={12}>
                            <Box sx={{ mb: 0.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                    {t('description')}
                                    <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
                                </Typography>
                            </Box>
                            <Controller
                                control={control}
                                name="desc"
                                render={({ field }) => (
                                    <>
                                        <TextField
                                            {...field}
                                            fullWidth
                                            multiline
                                            rows={3}
                                            size="small"
                                            placeholder={t('enterDescription')}
                                        />
                                        {errors.desc && (
                                            <FormHelperText error sx={{ ml: 0 }}>
                                                {errors.desc.message as string}
                                            </FormHelperText>
                                        )}
                                    </>
                                )}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                {/* Footer */}
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={onHide} color="inherit" disabled={showLoader}>
                        {t('close')}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={showLoader}
                        startIcon={showLoader ? <CircularProgress size={18} color="inherit" /> : null}
                    >
                        {t('saveChanges')}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default AddBankAccount;